import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Box, TextField, InputAdornment, Typography, Chip } from '@mui/material';
import QrCodeScannerOutlinedIcon from '@mui/icons-material/QrCodeScannerOutlined';
import KeyboardReturnOutlinedIcon from '@mui/icons-material/KeyboardReturnOutlined';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { isValidBarcode } from '../../helper/studio_helper';

interface BarcodeInputProps {
    value: string;
    onChange: (val: string) => void;
    onValidBarcodeEnter: (barcode: string) => void;
    inputRef?: React.RefObject<HTMLInputElement>;
    autoFocus?: boolean;
    className?: string;
    minWidth?: number | string;
    /** Min height of the outlined input row (default 48 — aligns with toolbar switches/buttons). */
    controlHeight?: number;
}

export const BarcodeInput = forwardRef<HTMLInputElement, BarcodeInputProps>(
    (
        {
            value,
            onChange,
            onValidBarcodeEnter,
            inputRef,
            autoFocus = false,
            className = '',
            minWidth = 320,
            controlHeight = 48,
        },
        ref
    ) => {
        const { t } = useTranslation();
        const [focused, setFocused] = useState(false);

        const internalRef = useRef<HTMLInputElement>(null);
        const effectiveRef = inputRef ?? internalRef;

        useImperativeHandle(ref, () => effectiveRef.current as HTMLInputElement, [effectiveRef]);

        const handleEnter = () => {
            if (!isValidBarcode(value)) {
                Swal.fire({
                    icon: 'error',
                    title: t('studio.itemAcceptanceCancellation.barcodeDigit'),
                    showConfirmButton: false,
                    timer: 1500,
                });
                onChange('');
                setTimeout(() => effectiveRef.current?.focus(), 0);
                return;
            }

            onValidBarcodeEnter(value);
            onChange('');
            setTimeout(() => effectiveRef.current?.focus(), 0);
        };

        const charCount = (value ?? '').length;
        const isComplete = charCount === 13;

        return (
            <Box
                className={className}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75,
                    minWidth,
                    flex: { xs: 1, lg: 'initial' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 0.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <QrCodeScannerOutlinedIcon sx={{ fontSize: '0.95rem', color: '#6B7280' }} />
                        <Typography
                            sx={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                color: '#6B7280',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                            }}
                        >
                            {t('studio.barcode')}
                        </Typography>
                    </Box>
                    <Chip
                        size="small"
                        label={`${charCount}/13`}
                        sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            color: isComplete ? '#15803D' : '#9CA3AF',
                            backgroundColor: isComplete ? '#DCFCE7' : '#F3F4F6',
                            border: `1px solid ${isComplete ? '#86EFAC' : '#E5E7EB'}`,
                            '& .MuiChip-label': { px: 0.75 },
                        }}
                    />
                </Box>
                <TextField
                    inputRef={effectiveRef}
                    value={value}
                    autoFocus={autoFocus}
                    placeholder={t('studio.itemAcceptanceCancellation.barcodeDigit')}
                    fullWidth
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    onKeyDown={(e) => e.key === 'Enter' && handleEnter()}
                    onChange={(e) => onChange(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start" sx={{ mr: 2.5, ml: 0.5 }}>
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: focused ? '#F3F4F6' : '#F9FAFB',
                                            color: focused ? '#475569' : '#6B7280',
                                            border: `1px solid ${focused ? '#CBD5E1' : '#E5E7EB'}`,
                                            transition: 'all 0.15s ease',
                                        }}
                                    >
                                        <QrCodeScannerOutlinedIcon sx={{ fontSize: '1.1rem' }} />
                                    </Box>
                                </InputAdornment>
                            ),
                            endAdornment: value ? (
                                <InputAdornment position="end">
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            px: 0.75,
                                            py: 0.25,
                                            borderRadius: '6px',
                                            backgroundColor: '#F9FAFB',
                                            border: '1px solid #E5E7EB',
                                            color: '#6B7280',
                                        }}
                                    >
                                        <KeyboardReturnOutlinedIcon sx={{ fontSize: '0.95rem' }} />
                                        <Typography
                                            sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.04em' }}
                                        >
                                            ENTER
                                        </Typography>
                                    </Box>
                                </InputAdornment>
                            ) : null,
                        },
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '10px',
                            backgroundColor: '#fff',
                            paddingLeft: '12px',
                            minHeight: controlHeight,
                            alignItems: 'center',
                            transition: 'all 0.18s ease',
                            '& fieldset': {
                                borderColor: '#E5E7EB',
                                borderWidth: '1.5px',
                            },
                            '&:hover fieldset': {
                                borderColor: '#CBD5E1',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#94A3B8',
                                boxShadow: '0 0 0 4px rgba(148, 163, 184, 0.22)',
                            },
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: '12px 14px 12px 6px',
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            color: '#111827',
                            '&::placeholder': {
                                color: '#9CA3AF',
                                fontWeight: 500,
                                fontSize: '0.85rem',
                                opacity: 1,
                            },
                        },
                    }}
                />
            </Box>
        );
    }
);

BarcodeInput.displayName = 'BarcodeInput';
